import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, IconButton, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { salesDemoDefaultsEndpoint } from "../../../api/endpoints";
import {
    ProposalDetail,
    ProposalExampleSlot,
} from "../../../components/ProposalFlow/types";
import { getVideoThumbnail } from "../../../utils/downloadHelpers";

// Internal-only demo coverage grid: one cell per (content type × selected
// channel). Filled cells show the resolved example (channel default →
// category default → newest demo fallback); empty cells invite the AM to
// add one. Picks write demo_video_defaults, so curation here improves every
// future proposal in the category.
interface ExampleCoverageGridProps {
    detail: ProposalDetail;
    slots: ProposalExampleSlot[];
    onChanged: () => void;
}

interface PickerTarget {
    contentTypeId: number;
    contentTypeName: string;
    channelId: number;
    channelName: string;
}

const SOURCE_LABEL: Record<string, string> = {
    "channel-default": "Channel default",
    "category-default": "Category default",
    fallback: "Newest demo",
};

function DemoVideoPickerDialog({
    target,
    categoryId,
    categoryName,
    onClose,
    onSaved,
}: {
    target: PickerTarget;
    categoryId: number;
    categoryName: string | null;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [search, setSearch] = useState("");
    const [videos, setVideos] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const params = new URLSearchParams({
                    limit: "24",
                    offset: "0",
                    productCategoryId: String(categoryId),
                });
                if (search.trim()) params.set("search", search.trim());
                const { data } = await axiosInstant.get(
                    `accountmanager/videos/tdc?${params.toString()}`,
                );
                if (!cancelled) setVideos(data.rows || []);
            } catch {
                if (!cancelled) setVideos([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 350);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [search, categoryId]);

    const setDefault = async (video: any, channelScoped: boolean) => {
        setSavingId(`${video.id}:${channelScoped ? "ch" : "cat"}`);
        try {
            await axiosInstant.post(salesDemoDefaultsEndpoint, {
                product_category_id: categoryId,
                content_type_id: target.contentTypeId,
                content_channel_id: channelScoped ? target.channelId : null,
                s3_file_id: video.id,
            });
            toast.success(
                channelScoped
                    ? `Set as the ${target.channelName} default`
                    : "Set as the category default",
            );
            onSaved();
        } catch {
            toast.error("Failed to save the default");
        } finally {
            setSavingId(null);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <IconButton
                onClick={onClose}
                sx={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}
                aria-label="Close"
            >
                <CloseIcon />
            </IconButton>
            <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>
                    Pick a demo example
                </Typography>
                <Typography sx={{ fontSize: 13, color: "var(--text-2)", mb: 2 }}>
                    {categoryName || "Category"} · {target.contentTypeName} · {target.channelName}
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search the video library…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 2 }}
                />
                {loading && (
                    <Typography sx={{ fontSize: 13, color: "var(--text-2)", py: 3, textAlign: "center" }}>
                        Loading videos…
                    </Typography>
                )}
                {!loading && videos.length === 0 && (
                    <Typography sx={{ fontSize: 13, color: "var(--text-2)", py: 3, textAlign: "center" }}>
                        No videos found for this category — clear the search or produce one first.
                    </Typography>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {videos.map((v) => (
                        <div key={v.id} className="border border-border-token rounded-lg overflow-hidden bg-surface">
                            <img
                                src={getVideoThumbnail(v)}
                                alt=""
                                className="w-full h-[96px] object-cover bg-surface-2"
                            />
                            <div className="p-2">
                                <div
                                    className="text-[12px] font-semibold text-text-1 truncate"
                                    title={v.name || v.key}
                                >
                                    {v.name || v.title || v.key}
                                </div>
                                <div className="text-[10.5px] text-text-2 truncate">
                                    {v.brandOwners?.[0]?.name || v.project?.brands?.[0]?.name || "—"}
                                    {v.demo ? " · demo" : ""}
                                </div>
                                <div className="flex gap-1.5 mt-1.5">
                                    <button
                                        type="button"
                                        disabled={!!savingId}
                                        onClick={() => setDefault(v, true)}
                                        className="flex-1 text-[10.5px] font-semibold text-white bg-accent rounded-md px-1.5 py-1 border-0 cursor-pointer disabled:opacity-60"
                                    >
                                        {savingId === `${v.id}:ch` ? "Saving…" : "This channel"}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={!!savingId}
                                        onClick={() => setDefault(v, false)}
                                        className="flex-1 text-[10.5px] font-semibold text-accent-text border border-accent bg-transparent rounded-md px-1.5 py-1 cursor-pointer disabled:opacity-60"
                                    >
                                        {savingId === `${v.id}:cat` ? "Saving…" : "Whole category"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function ExampleCoverageGrid({ detail, slots, onChanged }: ExampleCoverageGridProps) {
    const [picker, setPicker] = useState<PickerTarget | null>(null);
    const categoryId = detail.product_category ? detail.product_category.id : null;
    if (!categoryId || slots.length === 0) return null;

    const slotFor = (ctId: number, chId: number) =>
        slots.find((s) => s.content_type_id === ctId && s.content_channel_id === chId);

    const clearChannelDefault = async (ctId: number, chId: number) => {
        try {
            await axiosInstant.delete(salesDemoDefaultsEndpoint, {
                data: {
                    product_category_id: categoryId,
                    content_type_id: ctId,
                    content_channel_id: chId,
                },
            });
            toast.success("Channel default cleared");
            onChanged();
        } catch {
            toast.error("Failed to clear the default");
        }
    };

    return (
        <div className="mt-6">
            <h3 className="text-[16px] font-bold text-text-1 mb-1">Example coverage</h3>
            <p className="text-[12.5px] text-text-2 mb-3">
                The demo example shown for each video type × channel. Filling a slot updates the
                default for every {detail.product_category?.name?.toLowerCase() || "category"}{" "}
                proposal — internal only.
            </p>
            <div className="overflow-x-auto border border-border-token rounded-lg bg-surface">
                <table className="w-full border-collapse text-[12.5px]">
                    <thead>
                        <tr>
                            <th className="text-left px-3 py-2 text-[10.5px] font-semibold text-text-2 uppercase border-b border-border-token bg-surface-2">
                                Video type \ Channel
                            </th>
                            {detail.channels.map((ch) => (
                                <th
                                    key={ch.id}
                                    className="text-left px-3 py-2 text-[10.5px] font-semibold text-text-2 uppercase border-b border-border-token bg-surface-2"
                                >
                                    {ch.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {detail.content_type_units.map((u) => (
                            <tr key={u.content_type_id}>
                                <td className="px-3 py-2 border-b border-border-token font-semibold text-text-1 whitespace-nowrap">
                                    {u.display_name || u.name}
                                </td>
                                {detail.channels.map((ch) => {
                                    const slot = slotFor(u.content_type_id, ch.id);
                                    const video = slot?.video || null;
                                    return (
                                        <td key={ch.id} className="px-3 py-2 border-b border-border-token align-top">
                                            {video ? (
                                                <div className="w-[130px]">
                                                    <img
                                                        src={video.thumbnail || ""}
                                                        alt=""
                                                        className="w-[130px] h-[72px] object-cover rounded-md bg-surface-2"
                                                    />
                                                    <div className="flex items-center justify-between mt-1">
                                                        <span
                                                            className={`text-[9.5px] font-semibold px-1.5 py-[1px] rounded-full ${
                                                                slot?.source === "channel-default"
                                                                    ? "bg-up-bg text-up"
                                                                    : "bg-surface-2 text-text-2"
                                                            }`}
                                                        >
                                                            {SOURCE_LABEL[slot?.source || ""] || "—"}
                                                        </span>
                                                    </div>
                                                    <div className="flex gap-1 mt-1">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setPicker({
                                                                    contentTypeId: u.content_type_id,
                                                                    contentTypeName: u.display_name || u.name || "",
                                                                    channelId: ch.id,
                                                                    channelName: ch.name,
                                                                })
                                                            }
                                                            className="text-[10.5px] font-semibold text-accent-text bg-transparent border-0 cursor-pointer p-0"
                                                        >
                                                            Swap
                                                        </button>
                                                        {slot?.source === "channel-default" && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    clearChannelDefault(u.content_type_id, ch.id)
                                                                }
                                                                className="text-[10.5px] font-semibold text-text-2 bg-transparent border-0 cursor-pointer p-0"
                                                            >
                                                                · Clear
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setPicker({
                                                            contentTypeId: u.content_type_id,
                                                            contentTypeName: u.display_name || u.name || "",
                                                            channelId: ch.id,
                                                            channelName: ch.name,
                                                        })
                                                    }
                                                    className="w-[130px] h-[72px] rounded-md border border-dashed border-[#CFCAC2] text-accent-text text-[11.5px] font-semibold bg-surface cursor-pointer"
                                                >
                                                    + Add example
                                                </button>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {picker && (
                <DemoVideoPickerDialog
                    target={picker}
                    categoryId={categoryId}
                    categoryName={detail.product_category ? detail.product_category.name : null}
                    onClose={() => setPicker(null)}
                    onSaved={() => {
                        setPicker(null);
                        onChanged();
                    }}
                />
            )}
        </div>
    );
}
